import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FaChartBar, FaMedal, FaHandshake } from "react-icons/fa";
import { PageLoader } from "@/components/ui/page-loader";
import { useGroup } from "@/lib/context/GroupContext";
import { supabase } from "@/lib/supabase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DashboardLeaderboard from "@/components/dashboard/DashboardLeaderboard";
import DashboardChemistry from "@/components/dashboard/DashboardChemistry";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Match } from "@/utils/teamGenerator";

function normaliseMatch(raw: Record<string, unknown>): Match {
  return {
    ...(raw as unknown as Match),
    date: raw.match_date ? new Date(raw.match_date as string) : new Date(),
    teamA: (raw.teama as number[] | undefined) ?? (raw.teamA as number[] | undefined) ?? [],
    teamB: (raw.teamb as number[] | undefined) ?? (raw.teamB as number[] | undefined) ?? [],
    scoreA: (raw.scorea as number | null) ?? (raw.scoreA as number | null) ?? null,
    scoreB: (raw.scoreb as number | null) ?? (raw.scoreB as number | null) ?? null,
  };
}

export default function StatsPage() {
  const { t } = useTranslation();
  const { currentGroup } = useGroup();
  const { error: toastError } = useEnhancedToast();

  const groupId = currentGroup?.id;
  const [players, setPlayers] = useState<any[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);

  usePageTitle(`${t("v2.nav.stats")} — ${t("app.name")}`);

  const load = useCallback(async () => {
    if (!groupId) {
      setPlayers([]);
      setMatches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [{ data: rawPlayers, error: pErr }, { data: rawMatches, error: mErr }] =
        await Promise.all([
          supabase.from("players").select("*").eq("group_id", groupId).order("name"),
          supabase
            .from("matches")
            .select("*")
            .eq("group_id", groupId)
            .order("match_date", { ascending: false }),
        ]);

      if (pErr) throw pErr;
      if (mErr) throw mErr;

      setPlayers((rawPlayers ?? []) as any[]);
      setMatches(((rawMatches ?? []) as Record<string, unknown>[]).map(normaliseMatch));
    } catch (err) {
      console.error("StatsPage load error:", err);
      toastError({ title: t("errors.error"), description: t("v2.stats.loadError") });
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!groupId) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {t("groups.noGroupSelected")}
      </p>
    );
  }

  if (loading) {
    return <PageLoader label={t("common.loading")} />;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FaChartBar className="text-soccer-primary" />
          {t("v2.stats.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t("v2.stats.description")}</p>
      </div>

      <Tabs defaultValue="leaderboard">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="leaderboard" className="flex items-center gap-1.5">
            <FaMedal className="h-4 w-4" />
            {t("v2.stats.leaderboardTab")}
          </TabsTrigger>
          <TabsTrigger value="chemistry" className="flex items-center gap-1.5">
            <FaHandshake className="h-4 w-4" />
            {t("v2.stats.chemistryTab")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard" className="mt-4">
          <DashboardLeaderboard players={players} matches={matches} />
        </TabsContent>

        <TabsContent value="chemistry" className="mt-4">
          <DashboardChemistry players={players} matches={matches} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
